// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
import { type AskV2TransportApi } from "./useAskV2Transport";

// The four AskV2TransportApi members that reach the network — send, retry,
// submitEdit and abort — are each a request the backend answers with 403 for a
// read-only viewer, and `gateTransportWrites` below swaps all four for this
// no-op. Everything else on the API is local composer/edit state that never
// leaves the client. Resolving rather than rejecting keeps the local thread
// honest: no optimistic bubble, no failed-send retry affordance.
function refuseWrite(): Promise<void> {
  return Promise.resolve();
}

/**
 * Swap every network-reaching member of `AskV2TransportApi` for `refuseWrite`
 * when the viewer may not post. Exported so each override is pinned on its
 * own in AskV2ChatPage.test.tsx: `abort` was missing from this list for a
 * whole review cycle because only `send` was ever exercised through the page,
 * and `retry` / `submitEdit` have no UI path a page-level test can reach.
 *
 * Lives beside the transport it gates rather than in either page: both
 * AskV2ChatPage and AskV2ProjectPage need it, and routing one page's import
 * through the other would hang the transport's read-only contract off a
 * page module's import graph.
 *
 * NOT the authorization boundary. This gate — and the `readOnly` prop it
 * pairs with — is a client-side affordance: defence in depth over the
 * server's own checks, never a substitute for them. The chat writes it
 * swaps here ARE server-enforced (`assertChatInteractable` → 403, asserted
 * for `send` and per-row thumbs feedback in
 * qa/rickrubin/tests/api/ask-puntt-review.spec.ts). Two adjacent routes are
 * not: `createReviewComment` / `updateReviewComment`
 * (services/christo/app/handlers/catalyst/askPunttReview.ts) authorize on
 * enterprise scope alone — `loadReview` filters `{ _id, enterpriseId }` with
 * no owner / allowList / chat-membership check — so any enterprise member
 * holding a review id can still write a review comment on a chat they cannot
 * post to. That is pre-existing, outside this diff and tracked separately;
 * closing it for real needs a membership check inside those two handlers.
 * `canInteract` is read fail-open too (`?? true` in AskV2ChatPage.tsx /
 * AskV2ProjectPage.tsx), so during the history-load window the lock has not
 * engaged yet. Read a locked composer as an affordance, not as proof the
 * server refused.
 */
export function gateTransportWrites(
  transport: AskV2TransportApi,
  canInteract: boolean,
): AskV2TransportApi {
  if (canInteract) return transport;
  return {
    ...transport,
    send: refuseWrite,
    retry: refuseWrite,
    submitEdit: refuseWrite,
    // Stop is a write too: abortInFlight() POSTs abortMessage (403 here) and
    // its finally block then marks the turn "aborted" locally whether or not
    // the call succeeded — so an ungated Stop shows a read-only viewer a
    // cancelled turn the server is in fact still running.
    abort: refuseWrite,
  };
}
